import type { EmbedParams } from '@/store/global/initial-state';

type SearchParamsLike = {
  get: (name: string) => string | null;
};

export type EmbeddedAppKind = 'connector' | 'wiki';

const EMBED_QUERY_KEYS: Array<keyof EmbedParams> = [
  'app_id',
  'debug',
  'mode',
  'organization_id',
  'source',
  'workspace_id',
];

const toAppId = (value: string | null): number => {
  if (!value) return 0;

  const parsed = Number.parseInt(value, 10);
  return Number.isFinite(parsed) ? parsed : 0;
};

const appendEmbedParams = (params: URLSearchParams, embedParams?: EmbedParams | null) => {
  if (!embedParams) return;

  EMBED_QUERY_KEYS.forEach(key => {
    const value = embedParams[key];
    if (value == null || value === false) return;

    params.set(key, String(value));
  });
};

export const parseEmbedParams = (searchParams: SearchParamsLike): EmbedParams => ({
  app_id: toAppId(searchParams.get('app_id')),
  debug: searchParams.get('debug') === 'true',
  mode: searchParams.get('mode'),
  organization_id: searchParams.get('organization_id'),
  source: searchParams.get('source'),
  workspace_id: searchParams.get('workspace_id'),
});

export const buildEmbeddedChildUrl = (
  baseUrl: string,
  embedParams?: EmbedParams | null,
  lang?: string | null
): string => {
  if (!baseUrl) return baseUrl;

  try {
    const url = new URL(baseUrl);
    appendEmbedParams(url.searchParams, embedParams);

    if (embedParams?.mode === 'iframe') {
      url.searchParams.set('hide_header', 'true');
      url.searchParams.set('inplace_copilot', 'true');
    }

    if (lang && lang !== 'auto') {
      url.searchParams.set('lang', lang);
    }

    return url.toString();
  } catch {
    return baseUrl;
  }
};

export const buildEmbeddedHostPath = (
  app: EmbeddedAppKind,
  embedParams?: EmbedParams | null,
  returnTo?: string | null
): string => {
  const params = new URLSearchParams();

  appendEmbedParams(params, embedParams);

  if (returnTo) {
    params.set('returnTo', returnTo);
  }

  const query = params.toString();
  return query ? `/chat/embed/${app}?${query}` : `/chat/embed/${app}`;
};
